import { useState } from 'react';
import { UserService } from '@/api/services/userService';
import { FileUploadUtils } from '@/utils/file-upload-utils';
import {
  MediaUploadParams,
  MediaUploadResult,
  MediaDownloadParams,
  MediaDownloadResult,
  Attachment,
  AttachmentType,
  UploadStatus,
  AttachmentDownloadStatus
} from '@/api/types';
import { logPrint } from '@/utils/my-utils';

/**
 * 媒体文件管理相关的 hook
 * 处理文件上传下载URL获取等操作
 */
export function useFileMedia() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  /**
   * 根据文件类型自动检测媒体类型
   * @param file 文件对象
   * @returns 媒体类型
   */
  const detectMediaType = (file: File): 'image' | 'video' | 'audio' | 'file' | 'text' => {
    const mimeType = file.type.toLowerCase();
    
    if (mimeType.startsWith('image/')) {
      return 'image';
    } else if (mimeType.startsWith('video/')) {
      return 'video';
    } else if (mimeType.startsWith('audio/')) {
      return 'audio';
    } else if (mimeType.startsWith('text/')) {
      return 'text';
    } else {
      return 'file';
    }
  };

  /**
   * 获取媒体上传URL
   * @param params 上传参数
   * @returns Promise<MediaUploadResult | null> 上传URL和媒体ID
   */
  const getUploadUrl = async (params: MediaUploadParams): Promise<MediaUploadResult | null> => {
    setIsLoading(true);
    setError('');

    try {
      const result = await UserService.getUploadUrl(params);
      logPrint('上传URL获取成功:', result);
      return result;
    } catch (err: any) {
      console.error('获取上传URL失败:', err);
      const errorMessage = err?.err_msg || '获取上传URL失败';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 获取媒体下载URL
   * @param params 下载参数
   * @returns Promise<MediaDownloadResult | null> 下载URL
   */
  const getDownloadUrl = async (params: MediaDownloadParams): Promise<MediaDownloadResult | null> => {
    setIsLoading(true);
    setError('');

    try {
      const result = await UserService.getDownloadUrl(params);
      logPrint('下载URL获取成功');
      return result;
    } catch (err: any) {
      console.error('获取下载URL失败:', err);
      const errorMessage = err?.err_msg || '获取下载URL失败';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 上传文件到获取的URL（浏览器原生实现）
   * @param uploadUrl 上传URL
   * @param file 要上传的文件
   * @param onProgress 上传进度回调
   * @returns Promise<boolean> 是否上传成功
   */
  const uploadFileToUrl = async (
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<boolean> => {
    setIsLoading(true);
    setError('');

    const fileId = `${file.name}-${Date.now()}`;

    try {
      logPrint('使用浏览器原生文件上传');
      
      // 设置初始进度
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

      // 使用新的浏览器原生文件上传服务
      const result = await FileUploadUtils.uploadFileWithProgress(
        uploadUrl,
        file,
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
          onProgress?.(progress);
        }
      );

      if (result.success) {
        logPrint('浏览器原生上传成功');
        return true;
      } else {
        throw new Error(result.error || '文件上传失败');
      }

    } catch (err: any) {
      console.error('文件上传失败:', err);
      setError(err.message || '文件上传失败');
      return false;
    } finally {
      setIsLoading(false);
      // 清理进度记录
      setUploadProgress(prev => {
        const { [fileId]: removed, ...rest } = prev;
        return rest;
      });
    }
  };


  /**
   * 完整的文件上传流程（浏览器原生实现）
   * @param file 要上传的文件
   * @param options 上传选项
   * @param onProgress 上传进度回调
   * @returns Promise<{mediaId: string, accessUrl: string} | null> 上传成功返回媒体信息，失败返回null
   */
  const uploadFile = async (
    file: File,
    options: {
      conversationId?: string;
      context?: 'avatar' | 'conversation';
    } = {},
    onProgress?: (progress: number) => void
  ): Promise<{mediaId: string, accessUrl: string} | null> => {
    try {
      // 1. 获取上传URL
      const uploadResult = await getUploadUrl({
        conversation_id: options.conversationId,
        content_type: file.type || 'application/octet-stream',
        media_type: detectMediaType(file),
        size: file.size,
        context: options.context || 'avatar'
      });

      if (!uploadResult) {
        throw new Error('获取上传URL失败');
      }

      // 2. 上传文件（使用浏览器原生实现）
      const uploadSuccess = await uploadFileToUrl(
        uploadResult.upload_url,
        file,
        onProgress
      );

      if (!uploadSuccess) {
        throw new Error('文件上传失败');
      }

      // 3. 返回媒体信息
      return {
        mediaId: uploadResult.id,
        accessUrl: uploadResult.public_url
      };
    } catch (err: any) {
      console.error('完整上传流程失败:', err);
      setError(err.message || '上传失败');
      return null;
    }
  };

  /**
   * 批量上传文件
   * @param files 文件列表
   * @param options 上传选项
   * @param onProgress 整体进度回调
   * @returns Promise<{mediaId: string, accessUrl: string}[]> 成功上传的媒体信息列表
   */
  const uploadMultipleFiles = async (
    files: File[],
    options: {
      conversationId?: string;
      context?: 'avatar' | 'conversation';
    } = {},
    onProgress?: (completed: number, total: number) => void
  ): Promise<{mediaId: string, accessUrl: string}[]> => {
    const mediaInfos: {mediaId: string, accessUrl: string}[] = [];
    let completed = 0;

    for (const file of files) {
      try {
        const mediaInfo = await uploadFile(file, options);
        if (mediaInfo) {
          mediaInfos.push(mediaInfo);
        }
      } catch (err) {
        console.warn(`文件上传失败: ${file.name}`, err);
      } finally {
        completed++;
        onProgress?.(completed, files.length);
      }
    }

    return mediaInfos;
  };

  /**
   * 简化的头像上传方法
   * @param file 头像文件（可选，如果不提供则会打开文件选择器）
   * @param onProgress 上传进度回调
   * @returns Promise<{mediaId: string, accessUrl: string} | null>
   */
  const uploadAvatar = async (
    file?: File,
    onProgress?: (progress: number) => void
  ): Promise<{mediaId: string, accessUrl: string} | null> => {
    // 如果传入了文件，直接上传
    if (file) {
      return uploadFile(file, { context: 'avatar' }, onProgress);
    }
    
    // 如果没有传入文件，使用 Electron 文件选择器
    const result = await selectAndUploadImageWithAttachment(undefined, onProgress);
    return result ? {
      mediaId: result.fileId ?? "",
      accessUrl: result.url
    } : null;
  };

  /**
   * 简化的聊天文件上传方法
   * @param file 聊天文件
   * @param conversationId 会话ID
   * @param onProgress 上传进度回调
   * @returns Promise<{mediaId: string, accessUrl: string} | null>
   */
  const uploadChatFile = async (
    file: File,
    conversationId: string,
    onProgress?: (progress: number) => void
  ): Promise<{mediaId: string, accessUrl: string} | null> => {
    return uploadFile(file, { conversationId, context: 'conversation' }, onProgress);
  };

  /**
   * 清除错误信息
   */
  const clearError = () => {
    setError('');
  };

  /**
   * 清除所有上传进度
   */
  const clearUploadProgress = () => {
    setUploadProgress({});
  };

  /**
   * 仅选择单个文件（不上传）- 统一的文件选择入口
   * @param options 选择选项
   * @returns Promise<File | null> 选择的文件
   */
  const selectFile = async (options: {
    type?: 'image' | 'video' | 'audio' | 'file';
    title?: string;
  } = {}): Promise<File | null> => {
    try {
      const { type = 'image' } = options;
      
      // 定义不同类型的 accept 属性
      const acceptMap = {
        image: 'image/*',
        video: 'video/*',
        audio: 'audio/*',
        file: '*/*'
      };

      // 使用浏览器原生文件选择器
      const files = await FileUploadUtils.selectFiles({
        multiple: false,
        accept: acceptMap[type]
      });

      return files.length > 0 ? files[0] : null;
    } catch (err: any) {
      console.error('文件选择失败:', err);
      setError(err.message || '文件选择失败');
      return null;
    }
  };

  /**
   * 读取文件内容为 Base64 预览（仅用于预览）
   * @param file 文件对象
   * @returns Promise<string | null> Base64 数据 URL
   */
  const getFilePreview = async (file: File): Promise<string | null> => {
    try {
      const result = await FileUploadUtils.readFileAsBase64(file);
      return result;
    } catch (err: any) {
      console.error('获取文件预览失败:', err);
      setError(err.message || '获取文件预览失败');
      return null;
    }
  };

  /**
   * 选择并上传图片，返回 Attachment 对象
   */
  const selectAndUploadImageWithAttachment = async (
    conversationId?: string,
    _onProgress?: (progress: number) => void
  ): Promise<Attachment | null> => {
    // 使用原有的方法获取基本信息
    const basicResult = await selectAndUploadFileByType('image', conversationId, _onProgress);
    if (!basicResult) return null;
    
    return basicResult;
  };

  /**
   * 选择并上传视频
   */
  const selectAndUploadVideo = async (
    conversationId?: string,
    onProgress?: (progress: number) => void
  ): Promise<Attachment | null> => {
    const result = await selectAndUploadFileByType('video', conversationId, onProgress);
    return result;
  };

  /**
   * 选择并上传音频
   */
  const selectAndUploadAudio = async (
    conversationId?: string,
    onProgress?: (progress: number) => void
  ): Promise<Attachment | null> => {
    const result = await selectAndUploadFileByType('audio', conversationId, onProgress);
    return result;
  };

  /**
   * 选择并上传文件，返回 Attachment 对象
   */
  const selectAndUploadDocumentWithAttachment = async (
    conversationId?: string,
    onProgress?: (progress: number) => void
  ): Promise<Attachment | null> => {
    // 使用原有的方法获取基本信息
    const basicResult = await selectAndUploadFileByType('file', conversationId, onProgress);
    if (!basicResult) return null;
    
    return basicResult;
  };

  /**
   * 内部方法：根据类型选择并上传文件
   */
  const selectAndUploadFileByType = async (
    type: 'image' | 'video' | 'audio' | 'file',
    conversationId?: string,
    onProgress?: (progress: number) => void
  ): Promise<Attachment | null> => {
    setIsLoading(true);
    setError('');

    try {
      // 选择文件
      const selectedFile = await selectFile({ type });

      if (!selectedFile) {
        return null;
      }

      // 获取上传URL
      const uploadResult = await getUploadUrl({
        conversation_id: conversationId,
        content_type: selectedFile.type,
        media_type: type === 'file' ? 'file' : type,
        size: selectedFile.size,
        context: 'conversation'
      });

      if (!uploadResult) {
        throw new Error('获取上传URL失败');
      }

      // 上传文件
      const uploadSuccess = await uploadFileToUrl(
        uploadResult.upload_url,
        selectedFile,
        onProgress
      );

      if (!uploadSuccess) {
        throw new Error('文件上传失败');
      }

      // 获取文件扩展名
      const getFileExtension = (fileName: string): string => {
        const lastDot = fileName.lastIndexOf('.');
        return lastDot > 0 ? fileName.substring(lastDot + 1).toLowerCase() : '';
      };

      // 创建真实的 Attachment 对象
      const attachment: Attachment = {
        url: uploadResult.upload_url, // 真实的上传URL
        fileName: selectedFile.name, // 真实文件名
        fileSize: selectedFile.size, // 真实文件大小
        fileExtension: getFileExtension(selectedFile.name), // 真实扩展名
        type: type === 'image' ? AttachmentType.IMAGE : AttachmentType.DOCUMENT, // 根据类型设置
        uploadStatus: UploadStatus.UPLOADED, // 上传成功状态
        autoDownload: type === 'image', // 图片自动下载
        width: null, // 先设为 null，后面会更新
        height: null, // 先设为 null，后面会更新
        samples: null,
        duration: null,
        contactInfo: null,
        fileId: uploadResult.id, // 真实的文件ID
        thumbnail: null,
        downloadStatus: AttachmentDownloadStatus.NOT_DOWNLOADING,
        atUsers: null
      };

      // 如果是图片，尝试获取真实尺寸信息
      if (type === 'image') {
        try {
          const preview = await getFilePreview(selectedFile);
          
          if (preview) {
            const img = new Image();
            await new Promise<void>((resolve, reject) => {
              img.onload = () => {
                // 更新真实尺寸
                attachment.width = img.naturalWidth;
                attachment.height = img.naturalHeight;
                resolve();
              };
              img.onerror = reject;
              img.src = preview;
            });
          }
        } catch (error) {
          console.warn('获取图片尺寸失败:', error);
          // 不影响主流程，保持 width/height 为 null
        }
      }

      logPrint('创建的 Attachment 对象:', attachment);

      return attachment;

    } catch (err: any) {
      console.error('文件上传失败:', err);
      setError(err.message || '上传失败');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // 状态
    isLoading,
    error,
    uploadProgress,

    // 基础操作方法
    getUploadUrl,
    getDownloadUrl,
    uploadFileToUrl,

    // 完整上传方法（原有的，保持向后兼容）
    uploadFile,
    uploadMultipleFiles,

    // 简化上传方法（原有的）
    uploadAvatar,
    uploadChatFile,

    // Electron 专用方法（原有的，保持向后兼容）
    selectAndUploadVideo,
    selectAndUploadAudio,
    selectAndUploadFileByType,

    // 新增：返回 Attachment 对象的方法
    selectAndUploadImageWithAttachment,
    selectAndUploadDocumentWithAttachment,

    // 文件选择方法（统一入口）
    selectFile,
    getFilePreview,

    // 工具方法
    detectMediaType,
    clearError,
    clearUploadProgress
  };
}
